import { collectAssetIds } from '@burfy/docs-core';
import type { IndexedDbAssetStore } from './indexeddb-asset-store.ts';
import type { IndexedDbDocumentStore } from './indexeddb-document-store.ts';

/**
 * Delete image bytes no document refers to any more.
 *
 * Replacing or deleting an image cannot free its bytes at the time: undo would
 * restore a node pointing at an id that no longer resolves, and the same asset
 * may be used by another copy of the image, or by another document — the store
 * is shared. So nothing was ever freed, and ordinary editing could fill the
 * origin's quota, after which *every* insert fails.
 *
 * **At app start, and only then.** A fresh session has no undo history, so
 * nothing on screen can reach back to an asset that is unreferenced now. Run
 * mid-session, the same sweep would quietly break the next Ctrl+Z.
 *
 * Three rules keep it from being the thing that loses someone's pictures:
 *
 * 1. References come from **raw** records. A document the validator refuses
 *    still owns its images.
 * 2. If anything at all goes wrong reading them, nothing is deleted. A partial
 *    view of the references is indistinguishable from a document having none,
 *    and acting on it would delete exactly the images still in use.
 * 3. Assets stored in the last few minutes are left alone. Another tab can
 *    have imported one seconds ago and not yet autosaved the reference, and
 *    deleting those bytes would break an image in a document nobody touched.
 *    They are swept on a later start instead, which costs nothing.
 */

/**
 * How recently an asset must have been stored to be spared.
 *
 * Comfortably longer than an autosave debounce plus the write behind it, since
 * the only cost of being generous is that a few orphans wait for the next
 * start.
 */
export const SWEEP_GRACE_MS = 5 * 60 * 1000;

export interface SweepResult {
  /** Assets deleted. */
  removed: number;
  /** Assets still referenced. */
  kept: number;
  /** Set when the sweep declined to delete anything, with the reason. */
  skipped?: string;
}

export async function sweepAssets(
  documents: IndexedDbDocumentStore,
  assets: IndexedDbAssetStore,
  options: { now?: () => number } = {},
): Promise<SweepResult> {
  const now = options.now ?? (() => Date.now());
  let referenced: Set<string>;
  let stored: string[];

  try {
    const records = await documents.listRawRecords();
    referenced = new Set<string>();
    for (const record of records) collectAssetIds(record, referenced);

    stored = await assets.listIds();
  } catch (error) {
    // Deleting on a half-read view of the references is how a sweep becomes
    // the bug it was written to prevent.
    console.warn('[burfy-docs] asset sweep skipped: could not read references', error);
    return { removed: 0, kept: 0, skipped: 'references could not be read' };
  }

  const unreferenced = stored.filter((id) => !referenced.has(id));

  // Age is checked only for the assets nothing refers to, which is normally a
  // handful — reading it for all of them would mean deserializing every
  // image's bytes at start-up to look at a number.
  const cutoff = now() - SWEEP_GRACE_MS;
  const orphans: string[] = [];
  for (const id of unreferenced) {
    if ((await assets.storedAtOf(id)) < cutoff) orphans.push(id);
  }

  let removed = 0;

  for (const id of orphans) {
    try {
      await assets.remove(id);
      removed += 1;
    } catch (error) {
      // One stubborn record is not a reason to abandon the rest.
      console.warn('[burfy-docs] asset sweep could not remove', id, error);
    }
  }

  return { removed, kept: stored.length - removed };
}
